'use client';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import Image from 'next/image';
import {
  PlaySVG,
  PauseSVG,
  SoundOnIcon,
  SoundOffIcon
} from '../video_carousel/Icons';
import useVimeoPlayer from '../video_carousel/use-vimeo-player';

export default function VideoComponent({
  card,
  cardIndex,
  enableAutoplayTimer
}) {
  const [videoState, setVideoState] = useState({
    isPlaying: false,
    isMuted: true,
    hasAutoPlayed: false,
    shouldAutoplay: enableAutoplayTimer,
    showThumbnail: true
  });

  const [autoplayTimer, setAutoplayTimer] = useState(null);

  const {
    iframeRef,
    playerRef,
    isLoaded,
    isMuted,
    isPaused,
    isBuffering,
    remountKey
  } = useVimeoPlayer(card);

  // Reset state on unmount to start fresh on history changes
  useLayoutEffect(() => {
    return () => {
      setAutoplayTimer((timer) => {
        if (timer) clearTimeout(timer);
        return null;
      });
      setVideoState({
        isPlaying: false,
        isMuted: true,
        hasAutoPlayed: false,
        shouldAutoplay: enableAutoplayTimer,
        showThumbnail: true
      });
    };
  }, [enableAutoplayTimer]);

  useEffect(() => {
    if (!playerRef?.current || !isLoaded || !videoState.shouldAutoplay) return;

    const delay = cardIndex * 1000;

    const startAutoplay = setTimeout(() => {
      setVideoState((prev) => ({
        ...prev,
        isPlaying: true,
        shouldAutoplay: false
      }));

      playerRef.current
        .setMuted(true)
        .then(() => playerRef.current.play())
        .then(() => {
          setVideoState((prev) => ({
            ...prev,
            showThumbnail: false
          }));
        })
        .catch(() => {
          setVideoState((prev) => ({
            ...prev,
            isPlaying: false,
            shouldAutoplay: true
          }));
        });

      if (enableAutoplayTimer) {
        const timer = setTimeout(() => {
          setVideoState((prev) => ({
            ...prev,
            isPlaying: false,
            hasAutoPlayed: true
          }));

          if (playerRef.current) {
            playerRef.current.pause();
          }
        }, 10000);

        setAutoplayTimer(timer);
      }
    }, delay);

    return () => clearTimeout(startAutoplay);
  }, [
    isLoaded,
    playerRef,
    videoState.shouldAutoplay,
    cardIndex,
    enableAutoplayTimer
  ]);

  useEffect(() => {
    if (!playerRef?.current || !isLoaded || videoState.shouldAutoplay) return;

    if (videoState.isPlaying && isPaused) {
      playerRef.current.play().catch(() => {});
    } else if (!videoState.isPlaying && !isPaused) {
      playerRef.current.pause();
    }
  }, [
    videoState.isPlaying,
    videoState.shouldAutoplay,
    isPaused,
    isLoaded,
    playerRef
  ]);

  useEffect(() => {
    if (!playerRef?.current || !isLoaded) return;

    if (videoState.isMuted !== isMuted) {
      playerRef.current.setMuted(videoState.isMuted);
    }
  }, [videoState.isMuted, isMuted, isLoaded, playerRef]);

  useEffect(() => {
    return () => {
      if (autoplayTimer) {
        clearTimeout(autoplayTimer);
      }
    };
  }, [autoplayTimer]);

  const handlePlayClick = () => {
    if (autoplayTimer) {
      clearTimeout(autoplayTimer);
      setAutoplayTimer(null);
    }

    setVideoState((prev) => ({
      ...prev,
      isPlaying: !prev.isPlaying,
      showThumbnail: false,
      hasAutoPlayed: true,
      shouldAutoplay: false
    }));
  };

  const handleMuteToggle = () => {
    setVideoState((prev) => ({
      ...prev,
      isMuted: !prev.isMuted
    }));
  };

  const videoSrc = `${card.video_url.split('?')[0]}?background=1&autoplay=0&loop=0&muted=1&preload=1`;

  return (
    <div className="absolute inset-0 h-full w-full overflow-hidden">
      {videoState.showThumbnail && card.video_thumbnail && (
        <div
          className="absolute inset-0 z-30 cursor-pointer"
          onClick={handlePlayClick}
        >
          <Image
            src={card.video_thumbnail.url}
            alt={
              card.video_thumbnail.description ||
              card.video_thumbnail.title ||
              `Video thumbnail ${cardIndex}`
            }
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            priority={cardIndex < 2}
          />
          <div className="absolute inset-0 z-40 flex items-center justify-center">
            <PlaySVG />
          </div>
        </div>
      )}

      <iframe
        key={remountKey}
        ref={iframeRef}
        src={videoSrc}
        className={`pointer-events-none absolute border-0 max-md:top-1/2 max-md:left-1/2 max-md:h-[115%] max-md:w-[115%] max-md:-translate-x-1/2 max-md:-translate-y-1/2 md:inset-0 md:h-full md:w-full ${
          videoState.showThumbnail ? 'z-10' : 'z-20'
        }`}
        allow="autoplay; fullscreen"
        title={`Video player ${cardIndex}`}
        loading={cardIndex < 2 ? 'eager' : 'lazy'}
        style={{
          opacity: 1,
          aspectRatio: 'inherit'
        }}
      />

      {!videoState.showThumbnail && !videoState.hasAutoPlayed && (
        <button
          type="button"
          className="absolute inset-0 z-40 flex items-center justify-center"
          onClick={handlePlayClick}
          aria-label="Play video"
        >
          <PlaySVG />
        </button>
      )}

      {videoState.hasAutoPlayed &&
        !videoState.showThumbnail &&
        videoState.isPlaying && (
          <button
            type="button"
            className="absolute inset-0 z-60 flex items-center justify-center bg-transparent opacity-0 transition-opacity duration-300 lg:group-hover:opacity-100"
            onClick={handlePlayClick}
            aria-label="Pause video"
          >
            <PauseSVG />
          </button>
        )}

      {videoState.hasAutoPlayed &&
        !videoState.showThumbnail &&
        !videoState.isPlaying && (
          <button
            type="button"
            className="absolute inset-0 z-[60] flex items-center justify-center"
            onClick={handlePlayClick}
            aria-label="Play video"
          >
            <PlaySVG />
          </button>
        )}

      {videoState.isPlaying && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleMuteToggle();
          }}
          className="absolute top-2 right-2 z-[70] rounded p-1"
          aria-label={videoState.isMuted ? 'Unmute video' : 'Mute video'}
          type="button"
        >
          {videoState.isMuted ? (
            <SoundOffIcon className="h-10 w-10 text-white" />
          ) : (
            <SoundOnIcon className="h-10 w-10 text-white" />
          )}
        </button>
      )}

      {card.social_link?.href && card.social_link?.title && (
        <a
          className="body-l absolute top-1 left-2 z-[70] rounded px-2 py-1 text-white hover:underline"
          href={card.social_link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {card.social_link.title}
        </a>
      )}

      {isBuffering && videoState.isPlaying && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </div>
      )}
    </div>
  );
}
